import { progress } from "components/notification/subscribe"
import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import { diff_obj } from "data/lib/diff"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"
import { clearlocal, local, setlocal } from "data/lib/local-json"
import { logEvent } from "data/lib/log-event"
import { push_undo } from "data/lib/undo"
import notificationService from "data/notification"
import validationService from "data/validate"
import _ from "lodash"
import loadingService from "./loading"

export type Folder = {
  folderid: number
  parentfolderid: number
  code: string
  new?: boolean
  rename?: boolean
}

const id: string = "folder"
const getCurrentFolderId = () => _.last(local('folderPath'))
const initialState: ApiResult<object[]> = { id, state: "init", value: [] }
const folderStream = new BehaviorSubject<ApiResult<object[]>>(initialState)
let folderMap = {}
const current = () => folderStream.getValue()

function clear(): void {
  folderStream.next({ id, state: "ok", value: []})
  folderMap = {}
}

function get(folderid?: number): Folder {
  return folderMap[folderid || getCurrentFolderId()]
}

function onFolderNotFound(folders) {
  const folderid = _.last(local('folderPath'))
  if (!_.isNil(folderid) && !_.find(folders, {folderid})) {
    console.warn('folder not found');
    clearlocal('folderPath')
    clearlocal('property')
    window.location.reload()
  }
}

function fetchAllFolders(): Promise<object[] | { message: string }> {
  const currentValue: object[] = folderStream.getValue().value
  folderStream.next({ id, state: "busy", value: currentValue })

  return Client.get(id)
    .then((folders: object[]) => {
      validationService.fetchAll('folder')
      _.forEach(folders, f => folderMap[f.folderid] = f)
      folderStream.next({ id, state: "ok", value: folders })
      onFolderNotFound(folders)
      return folders
    })
    .catch((err: {message: string}) => {
      folderStream.next({ id, state: "error", error: err, value: currentValue })
      return err
    })
}

function fetchFolder(folderId: number): Promise<object | { message: string }> {
  const currentValue: object[] = folderStream.getValue().value
  folderStream.next({ id, state: "busy", value: currentValue })

  return Client.get(`folder?folderid=eq.${folderId}`)
    .then((folders: object[]) => {
      validationService.fetch('folder', folderId)
      const currentValue: object[] = folderStream.getValue().value
      // merge results unique by folderid
      folderStream.next({ id, state: "ok", value: _.unionBy(folders, currentValue, "folderid") })
      return folders[0]
    })
    .catch((err: {message: string}) => {
      folderStream.next({ id, state: "error", error: err, value: currentValue })
      return err
    })
}

function createFolder(parentFolderId?: number): Promise<string | Folder> {
  const current = folderStream.getValue().value
  const payload = {parentfolder: parentFolderId || null, _synchronous: true}
  const destination = {folderid: payload.parentfolder || undefined}
  let correlationid
  loadingService.toggleLoading(true, 'Creating Folder...')
  return logEvent('create-folder', {destination})
    .then((cid) => correlationid = cid)
    .then(() => Client.post(`msg/svc_create_folder`, payload, {headers:{correlationid}}))
    .then((folder: Folder) => {
      folderMap[folder.folderid] = folder
      // merge results unique by folderid
      _.set(folder, "rename", true)
      _.set(folder, "new", true)
      folderStream.next({ id, state: "ok", value: _.unionBy([folder], current, "folderid") })
      return folder
    })
    .catch((res: { error: { text: string } }) => { // these are old error signatures coming from the py helpers that we kept for backward compatibility with legacy
      logEvent('create-folder-failed', {destination}, correlationid)

      // on error overwrite optimistic update
      folderStream.next({ id, state: "error", error: res.error, value: current })
      notificationService.notify({ type: "danger", message: _.get(res, "error.text") || _.get(res, "message"), dismiss: 5000 })
      return _.get(res, "error.text")
    })
    .finally(() => loadingService.toggleLoading(false, 'Creating Folder...'))
}

function patchFolder(folder: Folder): Promise<object | { message: string }> {
  const current = folderStream.getValue()
  const folderid: number = folder.folderid
  const currentFolder = _.find(current.value, {folderid})
  const changeset = _.omit(diff_obj(folder, currentFolder), 'errordetail')
  if(_.isEmpty(changeset)) {
    if(folder?.rename) {
      delete folder.rename
      folderStream.next({ id, state: "ok", value: _.unionBy([folder], current.value, "folderid") })
    }
    return new Promise(() => current.value)
  }

  push_undo({
    undofunc: () => patchFolder(currentFolder),
    redofunc: () => patchFolder(folder)
  })

  if(folder.rename && folder.new && changeset.code) {
    changeset.name = changeset.code
  }
  delete folder.rename
  delete folder.new
  delete changeset.rename

  // optimistic update
  folderStream.next({ id, state: "ok", value: _.unionBy([folder], current.value, "folderid") })

  return Client.patch(`folder?folderid=eq.${folderid}`, changeset)
    .then((folders: object[]) => {
      validationService.fetch('folder', folderid)
      _.forEach(folders, f => {
        folderMap[f.folderid] = f
      })
      return folders[0]
    })
    .catch((err: {message: string}) => {
      // on error overwrite optimistic update
      folderStream.next({ id, state: "error", value: current.value, error: err })
      notificationService.notify({ type: "danger", message: err.message, dismiss: 5000 })
      return err
    })
}

function copyFolder(folderId: number, destinationId: number): Promise<void> {

  const payload = {
    folderId,
    destinationId,
    userId: _.get(JSON.parse(localStorage.getItem("auth.profile") || "{}"), "user_id"),
  }


  const component = progress('user', (event, update, unsubscribe, ctx) => {
    if (event.msg.correlationid != correlationid || event.msg.type != 'folder-copy') return
    const progress = event.msg.progress*100|0
    if(progress == 100 || event.eventType == 'FOLDER_COPY_FINISHED') {
      unsubscribe()
      // TODO: putting this here makes it less robust against page refresh
      notificationService.notify({ type: "info", message: `Copy of ${folderMap[folderId]?.code} complete.`, dismiss: 3000 })
    }
    update(progress)
  })
  notificationService.enqueue({ message: `Copying ${folderMap[folderId]?.code}`, correlationid: `${folderId}-${destinationId}`, component })

  const target = {folderid: folderId}
  const source = {folderid: _.get(_.find(current().value, (f) => f.folderid === folderId), "folderid")}
  const destination = {folderid: destinationId}
  let correlationid
  return logEvent('copy-folder', {target, source, destination})
    .then((cid) => correlationid = cid)
    .then(() => Client.post(`msg/svc_copy_folder`, payload, {headers: {correlationid}}))
    .then(() => fetchAllFolders())
    .catch((res: { error: { text: string } }) => { // these are old error signatures coming from the py helpers that we kept for backward compatibility with legacy
      logEvent('copy-folder-failed', {source, destination}, correlationid)
      // on error overwrite optimistic update
      folderStream.next({ id, state: "error", error: res.error, value: current().value })
      notificationService.notify({ type: "danger", message: _.get(res, "error.text"), dismiss: 5000 })
      return _.get(res, "error.text")
    })
}

function moveFolder(folderId: number, destinationId: number): Promise<void> {

  const payload = {
    destinationId,
    folderId,
    _synchronous: true
  }

  notificationService.notify({ type: "info", message: "Folder move initiated", dismiss: 3000 })

  const source = {folderid: folderId}
  const destination = {folderid: destinationId}
  let correlationid
  return logEvent('move-folder', {source, destination})
    .then((cid) => correlationid = cid)
    .then(() => Client.post(`msg/svc_move_folder`, payload, {headers: {correlationid}}))
    .then(() => fetchAllFolders())
    .catch((res: { error: { text: string } }) => { // these are old error signatures coming from the py helpers that we kept for backward compatibility with legacy
      logEvent('move-folder-failed', {source, destination}, correlationid)
      // on error overwrite optimistic update
      folderStream.next({ id, state: "error", error: res.error, value: current().value })
      notificationService.notify({ type: "danger", message: _.capitalize(_.get(res, "errors.[0]")), dismiss: 5000 })
      return _.get(res, "error.text")
    })
}

function deleteFolder(folderId: number): Promise<boolean | { message: string }> {
  const originVal = folderStream.getValue().value

  if (getCurrentFolderId() === folderId) {
    const message = "You cannot delete your current folder nor its parents"
    notificationService.notify({ type: "warn", message, dismiss: 2000 })
    return Promise.resolve(false)
  }

  // filter out the deleted propertyid
  folderStream.next({ id, state: "ok", value: _.filter(current().value, f => f.folderid !== folderId) })

  const payload = {
    folderId,
    _synchronous: true
  }

  // update the db
  const target = {folderid: folderId}
  let correlationid
  return logEvent('delete-folder', {target})
    .then((cid) => correlationid = cid)
    .then(() => Client.post(`msg/svc_delete_folder`, payload, {headers:{correlationid}}))
    .then((folders: Folder) => {
      // remove deleted folder from local cache
      _.forEach(folders, f => delete folderMap[f.folderId])
      // update already happened optimistically
      return true
    })
    .catch((res: { error: { text: string } }) => { // these are old error signatures coming from the py helpers that we kept for backward compatibility with legacy
      logEvent('delete-folder-failed', {target}, correlationid)

      // on error overwrite optimistic update
      folderStream.next({ id, state: "error", error: res.error, value: originVal })
      notificationService.notify({ type: "danger", message: _.get(res, "error.text"), dismiss: 5000 })
      return _.get(res, "error.text")
    })
}

function createSamples(): Promise<void> {
  return Client.post('migration/org-first-login', {})
}

async function fetchFolderPath(folderid: number): Promise<boolean> {
  return Client.get(`folderpath_v?folderid=eq.${folderid}`)
    .then((paths: [{idpath: []}]) => {
      return _.get(_.first(paths), "idpath")
    })
    .catch((err: {message: string}) => {
      notificationService.notify({ type: "danger", message: err.message, dismiss: 5000 })
      return false
    })
}

async function isAssetManagementSubfolder(folderid: number): Promise<boolean> {
  const amFolderRes = await Client.get('folder?code=eq.*AssetManagement')
  const amFolderId = _.get(_.first(amFolderRes), "folderid")
  return Client.get(`folderpath_v?folderid=eq.${folderid}`)
    .then((paths: [{idpath: []}]) => {
      const folderPath = _.get(_.first(paths), "idpath")
      const isSubfolder = _.includes(folderPath, amFolderId)
      return isSubfolder
    })
    .catch((err: {message: string}) => {
      notificationService.notify({ type: "danger", message: err.message, dismiss: 5000 })
      return false
    })
}

const setRenameTarget = (targetId: number) => {
  let newFolders = _.cloneDeep(folderStream.getValue().value)
  let target = _.find(newFolders, (f) => f.folderid === targetId)
  if(target) {
    _.set(target, "rename", true)
    folderStream.next({ id, state: "ok", value: _.unionBy([target], newFolders, "folderid") })
  }
}

const getCodePath = (folderid, path:string[]=[]) => {
  let folders = folderStream.getValue().value
  if(_.isEmpty(folders)) return path
  let folder = _.find(folders, (f) => f.folderid === folderid)
  if(!folder) return path
  if(!folder.parentfolder) {
    path.unshift(folder.code)
    return path
  } else {
    path.unshift(folder.code)
    return getCodePath(folder.parentfolder, path)
  }
}

const getIdPath = (folderid) => {
  let folders = folderStream.getValue().value
  if(_.isEmpty(folders)) return []
  let path: number[] = []
  let folder = folderMap[folderid]
  while (folder) {
    folderid = folder.folderid
    path.unshift(folderid)
    folder = folderMap[folder.parentfolder]
  }
  return path
}

const getFieldSubtree = (folderid, field, path:any[]=[]) => {
  let folders = folderStream.getValue().value
  if(_.isEmpty(folders)) return path
  let folder = _.find(folders, (f) => f.folderid === folderid)
  if(!folder) return path
  const findChildren = (fid) => {
    let children = _.filter(folders, (f) => f.parentfolder === fid)
    for(let i = 0; i < children.length; i++) {
      path.push(children[i][field])
      findChildren(children[i].folderid)
    }
  }

  // search up to top of current subtree then add all children
  if(!folder.parentfolder) {
    path.push(folder[field])
    findChildren(folder.folderid)
    return _.omit(_.uniq(path), undefined)
  } else {
    return getFieldSubtree(folder.parentfolder, field, path)
  }
}

const folderExplorerSearch = async (searchTerm: string) => {
  return Promise.all([Client.get(`folder?code=ilike.*${searchTerm}*`), Client.get(`property?code=ilike.*${searchTerm}*`)])
    .then(res => {
      const results = res.flat()
      return _.map(results, r => {
        const path = getCodePath(r.folderid)
        r.path = r.propertyid ? '/' + path.join('/') + `/${r.code}` : '/' + path.join('/')
        return r
      })
    })
}

const selectFolder = (fid: number) => {
  setlocal('folderPath', getIdPath(fid))
}

// TODO: When folder manipulation arrives, please include logEvent (see data/property).

// public module interface //

const debFetchAllFolders = _.debounce(fetchAllFolders, 5000, {leading: true, trailing: false})

const folderService = {
  stream: () => folderStream.asObservable(),
  value: () => folderStream.getValue().value,
  fetchAllFolders: () => debFetchAllFolders(),
  createFolder,
  createSamples,
  fetchFolder,
  fetchFolderPath,
  copyFolder,
  deleteFolder,
  moveFolder,
  get,
  getCurrentFolderId,
  getCodePath,
  getIdPath,
  getFieldSubtree,
  patchFolder,
  isAssetManagementSubfolder,
  selectFolder,
  setRenameTarget,
  folderExplorerSearch
}

export default folderService
